import React, { useState, useEffect } from 'react';
import {
  Heart,
  Eye,
  Star,
  ChevronLeft,
  ShoppingCart,
  ChevronRight,
  Plus,
  Minus,
  ArrowRight,
} from 'lucide-react';
import axios from 'axios';
import configs from '../config/config';
import { useNavigate } from 'react-router-dom';

// ─── Use the REAL context hook ───────────────────────────────
import { useCart } from '../context/CartContext';   // ← correct path

const ProductSection = ({ config, slug = '' }) => {
  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  // Use shared cart state from context
  const {
    addToCart,
    changeQuantity,
    isInCart,
    getQuantity,
  } = useCart();

  const [liked, setLiked] = useState({});

  const defaultConfig = {
    preset: 'classic-grid',
    title: 'Featured Products',
    subtitle: 'Discover our best-selling items',
    products: [],
    customStyles: {
      container: {
        padding: 'py-6 md:py-10',
        background: 'bg-white',
        titleColor: '#111827',
        textColor: '#111827',
        textAlign: 'left',
        fontSize: '2em',
        titlefontSize: 'text-2xl',
        subtitlefontSize: 'text-md',
        titlefontWeight: 'text-2xl',
        subtitlefontWeight: 'text-md',
        fontFamily: configs.FONT_FAMILY,
        borderRadius: '3px',
        featuredBg: configs.BASE_COLOR,
        cardBackground: 'bg-white',
        activeBg: 'bg-gray-900',
        titlePosition: 'text-left',
        subtitleColor: '#6B7280',
        accentColor: 'green',
      },
      cardHover: 'hover:shadow-lg',
    },
    grid: {
      mobileCols: 2,
      desktopCols: 6,
    },
    imageSize: {
      width: 400,
      height: 400,
    },
    cardHeight: 420,
    productLinkPattern: '/products/{id}',
  };

  const finalConfig = {
    ...defaultConfig,
    ...config,
    grid: { ...defaultConfig.grid, ...config?.grid },
    imageSize: { ...defaultConfig.imageSize, ...config?.imageSize },
    cardHeight: config?.cardHeight ?? defaultConfig.cardHeight,
  };

  const {
    preset,
    title,
    subtitle,
    products,
    customStyles,
    grid,
    imageSize,
    cardHeight,
  } = finalConfig;

  const containerClasses = [];
  const containerStyle = {};

  const bgValue = customStyles.container?.background || 'bg-white';
  if (bgValue.startsWith('bg-') || ['bg-white', 'bg-gray-50', 'bg-gray-100'].includes(bgValue)) {
    containerClasses.push(bgValue);
  } else {
    containerStyle.background = bgValue;
    if (bgValue.includes('url(')) {
      containerStyle.backgroundSize = 'cover';
      containerStyle.backgroundPosition = 'center';
      containerStyle.backgroundRepeat = 'no-repeat';
    }
  }

  if (customStyles.container?.padding) {
    containerClasses.push(customStyles.container.padding);
  }

  const finalContainerClass = containerClasses.join(' ');

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);

  const toggleLike = (cardId) => {
    setLiked((prev) => ({ ...prev, [cardId]: !prev[cardId] }));
  };

  const gridColsClass = {
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
    5: 'grid-cols-5',
    6: 'grid-cols-6',
  };

  const mobileColsClass = gridColsClass[grid.mobileCols] || 'grid-cols-2';
  const desktopColsClass = gridColsClass[grid.desktopCols] || 'grid-cols-6';


  useEffect(() => {
    localStorage.setItem("featuredBg", customStyles.container?.featuredBg || configs.BASE_COLOR);
     localStorage.setItem("activeBg", customStyles.container?.activeBg || configs.BASE_COLOR);
     
    }, [token]);
  const Header = () => (
    <div className="mb-6 md:mb-8">
      <h2
        className={`${customStyles.container?.titlefontSize} ${customStyles.container?.titlefontWeight}`}
        style={{
          color: customStyles.container?.titleColor,
          fontFamily: customStyles.container?.fontFamily,
          textAlign: customStyles.container?.textAlign,
        }}
      >
        {title}
      </h2>
      {subtitle && (
        <p
          className={`${customStyles.container?.subtitlefontSize} ${customStyles.container?.subtitlefontWeight} mt-1.5`}
          style={{
            color: customStyles.container?.subtitleColor,
            fontFamily: customStyles.container?.fontFamily,
            textAlign: customStyles.container?.textAlign,
          }}
        >
          {subtitle}
        </p>
      )}
    </div>
  );

  const ClassicGrid = () => (
    <div className={`${finalContainerClass} px-1 sm:px-6 lg:px-8`} style={containerStyle}>
      <div className="max-w-9xl mx-auto">
        <Header />

        <div className={`grid ${mobileColsClass} md:${desktopColsClass} gap-3 md:gap-4`}>
          {products.map((product) => {
            const qty = getQuantity(product.id);
            const inCart = isInCart(product.id);

            return (
              <div
                key={product.id}
                onClick={() => navigate(`/products/${product.id}`)}
                className={`${customStyles.container?.cardBackground} group overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer`}
                style={{
                  background: customStyles.container?.cardBackground,
                  borderRadius: customStyles.container?.borderRadius,
                }}
              >
                <div className="relative overflow-hidden aspect-square">
                  <img
                    src={product.image || 'https://placehold.co/400x400?text=No+Image'}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.src = 'https://placehold.co/400x400?text=Image+Not+Found';
                    }}
                  />

                  <div className="absolute inset-0 bg-gradient-to-br from-purple-600/10 to-pink-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                  {product.badge && (
                    <span className="absolute top-2 left-2 bg-purple-600 text-white px-2 py-0.5 rounded-md text-xs font-bold shadow-md">
                      {product.badge}
                    </span>
                  )}
                </div>

                <div className="p-3 md:p-4">
                  <h3
                    className="font-bold mb-2 line-clamp-2 min-h-[2.5rem]"
                    style={{
                      color: customStyles.container?.textColor,
                      fontSize: customStyles.container?.fontSize,
                      fontFamily: customStyles.container?.fontFamily,
                    }}
                  >
                    {product.name}
                  </h3>

                  <div className="flex items-baseline gap-1.5 mb-3">
                    <span
                      className={`text-lg md:text-xl font-bold ${customStyles.container?.accentColor}`}
                      style={{
                        color: customStyles.container?.accentColor || '#3b82f6',
                      }}
                    >
                      {formatCurrency(product.price)}
                    </span>

                    {product.originalPrice && (
                      <>
                        <span className="text-xs text-gray-400 line-through">
                          {formatCurrency(product.originalPrice)}
                        </span>
                        <span className="ml-auto text-xs font-semibold text-green-600 bg-green-50 px-1.5 py-0.5 rounded">
                          -{Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)}%
                        </span>
                      </>
                    )}
                  </div>

                  {!inCart ? (
                    <button
                      disabled={!product.inStock}
                      onClick={(e) => {
                        e.stopPropagation();
                        addToCart(product, 1);
                      }}
                      className={`w-full ${customStyles.container?.featuredBg} text-white rounded-lg flex items-center justify-between px-4 py-2.5 shadow-lg`}
                      style={{ background: customStyles.container?.featuredBg }}
                    >
                      <ShoppingCart className="w-3.5 h-3.5 md:w-4 md:h-4" />
                      {product.inStock ? 'Add to Cart' : 'Out of Stock'}
                    </button>
                  ) : (
                    <div
                      className={`w-full ${customStyles.container?.activeBg} text-white rounded-lg flex items-center justify-between px-4 py-2.5 shadow-lg`}
                      style={{ background: customStyles.container?.activeBg }}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          changeQuantity(product.id, -1);
                        }}
                        disabled={qty <= 1}
                        className="disabled:opacity-40 p-1"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="font-bold text-sm md:text-base">{qty}</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          changeQuantity(product.id, 1);
                        }}
                        className="p-1"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex justify-center mt-6 md:mt-8">
        <button
          onClick={() => navigate(`/shop/all/${slug}`)}
          className="bg-white border border-gray-300 rounded-lg px-5 py-2.5 hover:bg-gray-50 transition-all shadow-sm text-sm md:text-base font-medium"
        >
          View All Products
        </button>
      </div>
    </div>
  );

  const MinimalClean = () => (
    <div className={`${finalContainerClass} px-1 sm:px-6 lg:px-8`} style={containerStyle}>
      <div className="max-w-9xl mx-auto">
        <Header />

        <div className={`grid ${mobileColsClass} md:${desktopColsClass} gap-3 md:gap-4`}>
          {products.map((product) => {
            const qty = getQuantity(product.id);
            const inCart = isInCart(product.id);

            return (
              <div
                key={product.id}
                onClick={() => navigate(`/products/${product.id}`)}
                className={`${customStyles.container?.cardBackground} group relative overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer`}
                style={{
                  background: customStyles.container?.cardBackground,
                  borderRadius: customStyles.container?.borderRadius,
                }}
              >
                <div className="relative aspect-square">
                  <img
                    src={product.image || 'https://placehold.co/400x500?text=No+Image'}
                    alt={product.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.src = 'https://placehold.co/400x500?text=Image+Not+Found';
                    }}
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                  {product.badge && (
                    <span className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm text-purple-600 px-3 py-1 rounded-full text-xs font-bold shadow-md">
                      {product.badge}
                    </span>
                  )}

                  <div className="absolute bottom-0 left-0 right-0 p-4 transform translate-y-6 group-hover:translate-y-0 transition-transform duration-300">
                    <h3
                      className="font-bold mb-2 line-clamp-2"
                      style={{
                        color: customStyles.container?.textColor,
                        fontSize: customStyles.container?.fontSize,
                        fontFamily: customStyles.container?.fontFamily,
                      }}
                    >
                      {product.name}
                    </h3>

                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-lg md:text-xl font-bold ${customStyles.container?.accentColor}`}
                          style={{
                            color: customStyles.container?.accentColor || '#3b82f6',
                          }}
                        >
                          {formatCurrency(product.price)}
                        </span>
                        {product.originalPrice && (
                          <span className="text-xs text-gray-300 line-through">
                            {formatCurrency(product.originalPrice)}
                          </span>
                        )}
                      </div>
                    </div>

                    {!inCart ? (
                      <button
                        disabled={!product.inStock}
                        onClick={(e) => {
                          e.stopPropagation();
                          addToCart(product, 1);
                        }}
                        className={`w-full ${customStyles.container?.featuredBg} text-white rounded-lg flex items-center justify-between px-4 py-2.5 shadow-lg`}
                        style={{ background: customStyles.container?.featuredBg }}
                      >
                        <ShoppingCart className="w-4 h-4" />
                        {product.inStock ? 'Add to Cart' : 'Out of Stock'}
                      </button>
                    ) : (
                      <div
                        className={`w-full ${customStyles.container?.activeBg} text-white rounded-lg flex items-center justify-between px-4 py-2.5 shadow-lg`}
                        style={{ background: customStyles.container?.activeBg }}
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            changeQuantity(product.id, -1);
                          }}
                          disabled={qty <= 1}
                          className="disabled:opacity-40 p-1"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="font-bold text-base">{qty}</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            changeQuantity(product.id, 1);
                          }}
                          className="p-1"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex justify-center mt-6 md:mt-8">
        <button
          onClick={() => navigate(`/shop/all/${slug}`)}
          className="bg-white border border-gray-300 rounded-lg px-5 py-2.5 hover:bg-gray-50 transition-all shadow-sm text-sm md:text-base font-medium"
        >
          View All Products
        </button>
      </div>
    </div>
  );

  const Carousel = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);

    const getItemsPerView = () => {
      if (typeof window === 'undefined') return 4;
      if (window.innerWidth < 640) return 2;
      if (window.innerWidth < 1024) return 3;
      return Math.min(grid.desktopCols || 5, 6);
    };

    const [itemsPerView, setItemsPerView] = useState(getItemsPerView());

    useEffect(() => {
      const handleResize = () => setItemsPerView(getItemsPerView());
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, [grid.desktopCols]);

    const maxIndex = Math.max(0, products.length - itemsPerView);

    const nextSlide = () => {
      if (isTransitioning) return;
      setIsTransitioning(true);
      setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
      setTimeout(() => setIsTransitioning(false), 500);
    };

    const prevSlide = () => {
      if (isTransitioning) return;
      setIsTransitioning(true);
      setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
      setTimeout(() => setIsTransitioning(false), 500);
    };

    return (
      <div className={`${finalContainerClass} relative px-1 sm:px-6 lg:px-8`} style={containerStyle}>
        <div className="max-w-9xl mx-auto">
          <div className="mb-6 md:mb-8">
            <div className="flex items-center justify-between gap-4">
              <Header />
              <a
                onClick={() => navigate(`/shop/all/${slug}`)}
                className={`text-sm md:text-base font-medium cursor-pointer whitespace-nowrap hover:underline transition-colors ${customStyles.container?.accentColor}`}
                style={{
                  color: customStyles.container?.accentColor || '#3b82f6',
                }}
              >
                <strong>View All →</strong>
              </a>
            </div>
          </div>

          <div className="relative overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-out"
              style={{
                transform: `translateX(-${(currentIndex * 100) / itemsPerView}%)`,
              }}
            >
              {products.map((product, idx) => {
                const qty = getQuantity(product.id);
                const inCart = isInCart(product.id);

                return (
                  <div
                    key={product.id || idx}
                    className="flex-shrink-0 px-2"
                    style={{ width: `${100 / itemsPerView}%` }}
                  >
                    <div
                      onClick={() => navigate(`/products/${product.id}`)}
                      className={`${customStyles.container?.cardBackground} group overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer`}
                      style={{
                        background: customStyles.container?.cardBackground,
                        borderRadius: customStyles.container?.borderRadius,
                      }}
                    >
                      <div className="relative overflow-hidden aspect-square">
                        <img
                          src={product.image || 'https://placehold.co/400x500?text=No+Image'}
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.src = 'https://placehold.co/400x500?text=Image+Not+Found';
                          }}
                        />

                        {product.badge && (
                          <span className="absolute top-3 left-3 bg-purple-600 text-white px-2.5 py-1 rounded-full text-xs font-semibold shadow-md">
                            {product.badge}
                          </span>
                        )}

                        <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/products/${product.id}`);
                            }}
                            className="bg-white p-2 rounded-full shadow-lg hover:bg-purple-600 hover:text-white transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      <div className="p-3 md:p-4">
                        <h3
                          className="font-bold mb-2 line-clamp-2 min-h-[2.5rem]"
                          style={{
                            color: customStyles.container?.textColor,
                            fontSize: customStyles.container?.fontSize,
                            fontFamily: customStyles.container?.fontFamily,
                          }}
                        >
                          {product.name}
                        </h3>

                        <div className="flex items-center gap-1.5 mb-3">
                          <span
                            className={`text-lg md:text-xl font-bold ${customStyles.container?.accentColor}`}
                            style={{
                              color: customStyles.container?.accentColor || '#3b82f6',
                            }}
                          >
                            {formatCurrency(product.price)}
                          </span>

                          {product.originalPrice && (
                            <span className="text-xs text-gray-400 line-through">
                              {formatCurrency(product.originalPrice)}
                            </span>
                          )}
                        </div>

                        {!inCart ? (
                          <button
                            disabled={!product.inStock}
                            onClick={(e) => {
                              e.stopPropagation();
                              addToCart(product, 1);
                            }}
                            className={`w-full ${customStyles.container?.featuredBg} text-white rounded-lg flex items-center justify-between px-4 py-2.5 shadow-lg`}
                            style={{ background: customStyles.container?.featuredBg }}
                          >
                            {product.inStock ? 'Add to Cart' : 'Out of Stock'}
                          </button>
                        ) : (
                          <div
                            className={`w-full ${customStyles.container?.activeBg} text-white rounded-lg flex items-center justify-between px-4 py-2.5 shadow-lg`}
                            style={{ background: customStyles.container?.activeBg }}
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                changeQuantity(product.id, -1);
                              }}
                              disabled={qty <= 1}
                              className="disabled:opacity-40 p-1"
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <span className="font-bold text-sm">{qty}</span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                changeQuantity(product.id, 1);
                              }}
                              className="p-1"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {products.length > itemsPerView && (
              <>
                <button
                  onClick={prevSlide}
                  disabled={isTransitioning}
                  className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 bg-white/95 backdrop-blur-sm p-2 rounded-full shadow-lg hover:bg-white hover:scale-110 transition-all duration-200 z-10 border border-gray-200/50 disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4 md:w-5 md:h-5 text-gray-800" />
                </button>
                <button
                  onClick={nextSlide}
                  disabled={isTransitioning}
                  className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 bg-white/95 backdrop-blur-sm p-2 rounded-full shadow-lg hover:bg-white hover:scale-110 transition-all duration-200 z-10 border border-gray-200/50 disabled:opacity-50"
                >
                  <ChevronRight className="w-4 h-4 md:w-5 md:h-5 text-gray-800" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={finalContainerClass} style={containerStyle}>
      {preset === 'classic-grid' && <ClassicGrid />}
      {preset === 'minimal-clean' && <MinimalClean />}
      {preset === 'carousel' && <Carousel />}
    </div>
  );
};

export default ProductSection;